import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { storage, STORAGE_KEYS, generateId } from '../utils/storage';
import { useGymInfra } from './GymInfraContext';
import { useUI } from './UIContext';
import { supabase } from '../lib/supabase';
import { subDays, format, startOfMonth, endOfMonth } from 'date-fns';
import {
  dbAddMember, dbUpdateMember, dbDeleteMember,
  dbAddTrainer, dbUpdateTrainer, dbDeleteTrainer,
  dbAddAttendance, dbDeleteAttendance,
  dbApproveMember, dbRejectMember,
} from '../lib/db';
import { toCamel } from '../utils/helpers';

const MemberContext = createContext(null);

export const MemberProvider = ({ children }) => {
  const { gymId, useDB, isDemoMode, reloadTrigger } = useGymInfra();
  const { showToast } = useUI();

  const [members, setMembers] = useState([]);
  const [trainers, setTrainers] = useState([]);
  const [attendances, setAttendances] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // DB 로드
  useEffect(() => {
    if (useDB && gymId) {
      (async () => {
        try {
          const [{ data: m }, { data: t }, { data: a }] = await Promise.all([
            supabase.from('members').select('*').eq('gym_id', gymId).order('created_at', { ascending: false }),
            supabase.from('trainers').select('*').eq('gym_id', gymId),
            supabase.from('attendances').select('*').eq('gym_id', gymId).gte('date', format(subDays(new Date(), 365), 'yyyy-MM-dd')).order('created_at', { ascending: false }),
          ]);
          if (m) {
            setMembers(toCamel(m).map(x => ({ ...x, createdAt: (x.createdAt || '').slice(0, 10) })));
          }
          if (t) setTrainers(toCamel(t));
          if (a) setAttendances(toCamel(a));
        } catch (e) {
          console.error('MemberContext DB 로드 실패:', e);
        } finally {
          setLoaded(true);
        }
      })();
    } else if (isDemoMode) {
      setMembers(storage.get(STORAGE_KEYS.MEMBERS) || []);
      setTrainers(storage.get(STORAGE_KEYS.TRAINERS) || []);
      setAttendances(storage.get(STORAGE_KEYS.ATTENDANCES) || []);
      setLoaded(true);
    } else {
      setLoaded(true);
    }
  }, [useDB, gymId, isDemoMode, reloadTrigger]); // eslint-disable-line react-hooks/exhaustive-deps

  // ================================================================
  // 회원 CRUD
  // ================================================================
  const addMember = useCallback(async (memberData) => {
    if (useDB) {
      try {
        const newMember = await dbAddMember(gymId, memberData);
        setMembers(prev => [newMember, ...prev]);
        showToast('회원이 등록되었습니다.');
        return newMember;
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newMember = { ...memberData, id: generateId(), createdAt: new Date().toISOString().split('T')[0] };
      const updated = [...members, newMember];
      storage.set(STORAGE_KEYS.MEMBERS, updated);
      setMembers(updated);
      showToast('회원이 등록되었습니다.');
      return newMember;
    }
  }, [useDB, gymId, members, showToast]);

  const updateMember = useCallback(async (id, memberData) => {
    if (useDB) {
      try {
        await dbUpdateMember(id, memberData);
        setMembers(prev => prev.map(m => m.id === id ? { ...m, ...memberData } : m));
        showToast('회원 정보가 수정되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = members.map(m => m.id === id ? { ...m, ...memberData } : m);
      storage.set(STORAGE_KEYS.MEMBERS, updated);
      setMembers(updated);
      showToast('회원 정보가 수정되었습니다.');
    }
  }, [useDB, members, showToast]);

  const deleteMember = useCallback(async (id) => {
    if (useDB) {
      try {
        await dbDeleteMember(id);
        setMembers(prev => prev.filter(m => m.id !== id));
        showToast('회원이 삭제되었습니다.', 'error');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = members.filter(m => m.id !== id);
      storage.set(STORAGE_KEYS.MEMBERS, updated);
      setMembers(updated);
      showToast('회원이 삭제되었습니다.', 'error');
    }
  }, [useDB, members, showToast]);

  // 외부 Context에서 members를 직접 수정할 수 있는 함수 (DB 호출 없이 setState만)
  const patchMemberLocal = useCallback((id, data) => {
    setMembers(prev => prev.map(m => m.id === id ? { ...m, ...data } : m));
  }, []);

  // ================================================================
  // 트레이너 CRUD
  // ================================================================
  const addTrainer = useCallback(async (data) => {
    if (useDB) {
      try {
        const newTrainer = await dbAddTrainer(gymId, data);
        setTrainers(prev => [...prev, newTrainer]);
        showToast('트레이너가 등록되었습니다.');
        return newTrainer;
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const newTrainer = { ...data, id: generateId(), createdAt: new Date().toISOString().split('T')[0] };
      const updated = [...trainers, newTrainer];
      storage.set(STORAGE_KEYS.TRAINERS, updated);
      setTrainers(updated);
      showToast('트레이너가 등록되었습니다.');
      return newTrainer;
    }
  }, [useDB, gymId, trainers, showToast]);

  const updateTrainer = useCallback(async (id, data) => {
    if (useDB) {
      try {
        await dbUpdateTrainer(id, data);
        setTrainers(prev => prev.map(t => t.id === id ? { ...t, ...data } : t));
        showToast('트레이너 정보가 수정되었습니다.');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = trainers.map(t => t.id === id ? { ...t, ...data } : t);
      storage.set(STORAGE_KEYS.TRAINERS, updated);
      setTrainers(updated);
      showToast('트레이너 정보가 수정되었습니다.');
    }
  }, [useDB, trainers, showToast]);

  const deleteTrainer = useCallback(async (id) => {
    if (useDB) {
      try {
        await dbDeleteTrainer(id);
        setTrainers(prev => prev.filter(t => t.id !== id));
        showToast('트레이너가 삭제되었습니다.', 'error');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = trainers.filter(t => t.id !== id);
      storage.set(STORAGE_KEYS.TRAINERS, updated);
      setTrainers(updated);
      showToast('트레이너가 삭제되었습니다.', 'error');
    }
  }, [useDB, trainers, showToast]);

  // ================================================================
  // 출석 CRUD
  // ================================================================
  const addAttendance = useCallback(async (memberId) => {
    const today = new Date();
    const dateStr = today.toISOString().split('T')[0];
    const timeStr = today.toTimeString().slice(0, 5);
    const alreadyChecked = attendances.some(a => a.memberId === memberId && a.date === dateStr);
    if (alreadyChecked) { showToast('이미 오늘 출석한 회원입니다.', 'error'); return null; }

    if (useDB) {
      try {
        const newAtt = await dbAddAttendance(gymId, memberId, dateStr, timeStr);
        setAttendances(prev => [newAtt, ...prev]);
        showToast('출석 체크가 완료되었습니다.');
        return newAtt;
      } catch (e) { showToast(e.message, 'error'); return null; }
    } else {
      const newAttendance = { id: generateId(), memberId, date: dateStr, time: timeStr, createdAt: today.toISOString() };
      const updated = [...attendances, newAttendance];
      storage.set(STORAGE_KEYS.ATTENDANCES, updated);
      setAttendances(updated);
      showToast('출석 체크가 완료되었습니다.');
      return newAttendance;
    }
  }, [useDB, gymId, attendances, showToast]);

  const deleteAttendance = useCallback(async (id) => {
    if (useDB) {
      try {
        await dbDeleteAttendance(id);
        setAttendances(prev => prev.filter(a => a.id !== id));
        showToast('출석이 취소되었습니다.', 'error');
      } catch (e) { showToast(e.message, 'error'); }
    } else {
      const updated = attendances.filter(a => a.id !== id);
      storage.set(STORAGE_KEYS.ATTENDANCES, updated);
      setAttendances(updated);
      showToast('출석이 취소되었습니다.', 'error');
    }
  }, [useDB, attendances, showToast]);

  // ================================================================
  // 출석 월별 추가 로드 (1년 이전 데이터 on-demand)
  // ================================================================
  const fetchAttendancesByMonth = useCallback(async (year, month) => {
    if (!useDB || !gymId) return;
    const target = new Date(year, month - 1, 1);
    const from = format(startOfMonth(target), 'yyyy-MM-dd');
    const to = format(endOfMonth(target), 'yyyy-MM-dd');
    // 이미 ���당 월 데이터가 있으면 스킵
    if (attendances.some(a => a.date >= from && a.date <= to)) return;
    try {
      const { data } = await supabase
        .from('attendances')
        .select('*')
        .eq('gym_id', gymId)
        .gte('date', from)
        .lte('date', to)
        .order('created_at', { ascending: false });
      if (data?.length) {
        setAttendances(prev => {
          const ids = new Set(prev.map(a => a.id));
          const newItems = toCamel(data).filter(a => !ids.has(a.id));
          return [...prev, ...newItems];
        });
      }
    } catch (e) {
      console.error('출석 월별 로드 실패:', e);
    }
  }, [useDB, gymId, attendances]);

  // ================================================================
  // 가입 신청 처리
  // ================================================================
  const approveMember = useCallback(async (id) => {
    if (!useDB) return;
    try {
      await dbApproveMember(id);
      setMembers(prev => prev.map(m => m.id === id ? { ...m, status: 'active' } : m));
      showToast('가입이 승인되었습니다.');
    } catch (e) { showToast(e.message, 'error'); }
  }, [useDB, showToast]);

  const rejectMember = useCallback(async (id) => {
    if (!useDB) return;
    try {
      await dbRejectMember(id);
      setMembers(prev => prev.filter(m => m.id !== id));
      showToast('가입이 거절되었습니다.', 'error');
    } catch (e) { showToast(e.message, 'error'); }
  }, [useDB, showToast]);

  return (
    <MemberContext.Provider value={{
      members, addMember, updateMember, deleteMember, patchMemberLocal,
      trainers, addTrainer, updateTrainer, deleteTrainer,
      attendances, addAttendance, deleteAttendance, fetchAttendancesByMonth,
      approveMember, rejectMember,
      _memberLoaded: loaded,
    }}>
      {children}
    </MemberContext.Provider>
  );
};

export const useMembers = () => {
  const ctx = useContext(MemberContext);
  if (!ctx) throw new Error('useMembers must be used within MemberProvider');
  return ctx;
};
